/**
 * LeadForge France - Main App
 * Modular, scalable, configurable
 */
import React, { useEffect } from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './lib/auth';
import { ConfigProvider, useConfig } from './lib/config';
import { initGA4, trackPageView } from './lib/analytics';
import { captureAttribution } from './lib/attribution';
import './App.css';

// Layout components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import { lazy, Suspense } from 'react';
const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const CategoriesPage = lazy(() => import('./pages/CategoriesPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const PurchasePage = lazy(() => import('./pages/PurchasePage'));
const SellPage = lazy(() => import('./pages/SellPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminEnrichmentPage = lazy(() => import('./pages/AdminEnrichmentPage'));
const AdminEnrichmentBatchPage = lazy(() => import('./pages/AdminEnrichmentBatchPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const PricingFullPage = lazy(() => import('./pages/PricingFullPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const CGVPage = lazy(() => import('./pages/CGVPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const MentionsPage = lazy(() => import('./pages/MentionsPage'));
const CheckoutCancelPage = lazy(() => import('./pages/CheckoutCancelPage'));
const SecteurPage = lazy(() => import('./pages/SecteurPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const CreationSiteWebPage = lazy(() => import('./pages/CreationSiteWebPage'));
import SEOLandingPage from './pages/SEOLandingPage';


const NOINDEX_PATHS = new Set([
  '/login', '/register', '/dashboard', '/admin', '/checkout',
  '/checkout/success', '/checkout/cancel', '/auth/callback',
  '/forgot-password', '/reset-password', '/orders', '/purchase', '/settings'
]);

const RobotsMeta = () => {
  const location = useLocation();
  const path = location.pathname;
  const isPrivate = NOINDEX_PATHS.has(path) || path.startsWith('/orders/') || path.startsWith('/admin/');
  if (!isPrivate) return null;
  return (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );
};

// SEO Page Router - handles /leads-{category}-{location} patterns
const SEOPageRouter = () => {
  const { slug } = useParams();

  if (slug && slug.startsWith('leads-')) {
    return <SEOLandingPage />;
  }

  return (
    <div className="container-custom py-16 text-center">
      <h1 className="text-6xl font-bold text-[#E2E8F0] mb-4">404</h1>
      <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Page non trouvée</h2>
      <p className="text-[#64748B] mb-8">
        La page que vous recherchez n'existe pas.
      </p>
      <a href="/" className="text-[#2563EB] hover:underline">
        Retour à l'accueil
      </a>
    </div>
  );
};


// Redirect /leads/:slug to static HTML pages served by nginx
const LeadsStaticPage = () => {
  const { slug } = useParams();
  useEffect(() => {
    window.location.reload();
  }, [slug]);
  return null;
};

// Analytics wrapper
const AnalyticsWrapper = ({ children }) => {
  const location = useLocation();
  const { config } = useConfig();

  // Capture first-touch du canal d'acquisition (UTM + referrer + landing)
  useEffect(() => {
    captureAttribution();
  }, []);

  useEffect(() => {
    if (config.ga4_measurement_id) {
      initGA4(config.ga4_measurement_id);
    }
  }, [config.ga4_measurement_id]);

  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);

  return children;
};

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Admin route wrapper (2026-09-19, correctif securite) : reutilise isAdmin
// deja expose par useAuth() (base sur le role JWT existant, get_current_admin
// cote serveur reste le VRAI rempart -- ce garde cote client n'est qu'un
// confort UX, jamais la seule protection). Aucun nouveau systeme de roles.
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Layout wrapper for pages with header/footer
const MainLayout = ({ children }) => {
  const location = useLocation();
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* key = pathname : l'erreur est "oubliée" dès qu'on change de page */}
        <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
      </main>
      <Footer />
    </div>
  );
};

// Auth layout (no header/footer)
const AuthLayout = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
};

function AppRoutes() {
  return (
    <><RobotsMeta />
    <Suspense fallback={<div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh'}}><div style={{width:40,height:40,border:'4px solid #E2E8F0',borderTop:'4px solid #2563EB',borderRadius:'50%',animation:'spin 1s linear infinite'}}></div></div>}>
    <Routes>
      <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
      <Route path="/sell" element={<MainLayout><SellPage /></MainLayout>} />
        <Route path="/orders" element={<OrdersPage />} />
      <Route path="/pricing-full" element={<MainLayout><PricingFullPage /></MainLayout>} />
      <Route path="/checkout" element={<MainLayout><CheckoutPage /></MainLayout>} />
      <Route path="/admin" element={<AdminRoute><MainLayout><AdminDashboard /></MainLayout></AdminRoute>} />
      <Route path="/admin/enrichment" element={<AdminRoute><MainLayout><AdminEnrichmentPage /></MainLayout></AdminRoute>} />
      <Route path="/admin/enrichment/batch" element={<AdminRoute><MainLayout><AdminEnrichmentBatchPage /></MainLayout></AdminRoute>} />
      <Route path="/admin/enrichment/batch/:batchId" element={<AdminRoute><MainLayout><AdminEnrichmentBatchPage /></MainLayout></AdminRoute>} />
      <Route path="/admin/enrichment/:siret" element={<AdminRoute><MainLayout><AdminEnrichmentPage /></MainLayout></AdminRoute>} />
      <Route path="/categories" element={<MainLayout><CategoriesPage /></MainLayout>} />
      <Route path="/pricing" element={<MainLayout><PricingPage /></MainLayout>} />
      <Route path="/purchase" element={<MainLayout><PurchasePage /></MainLayout>} />
      <Route
        path="/forgot-password"
        element={
          <AuthLayout>
            <ForgotPasswordPage />
          </AuthLayout>
        }
      />
      <Route
        path="/reset-password"
        element={
          <AuthLayout>
            <ResetPasswordPage />
          </AuthLayout>
        }
      />
      <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
      <Route path="/register" element={<AuthLayout><RegisterPage /></AuthLayout>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/checkout/success" element={<AuthLayout><CheckoutSuccessPage /></AuthLayout>} />
      <Route path="/checkout/cancel" element={<AuthLayout><CheckoutCancelPage /></AuthLayout>} />
      <Route path="/dashboard" element={<ProtectedRoute><MainLayout><DashboardPage /></MainLayout></ProtectedRoute>} />
      <Route path="/orders/:orderId" element={<ProtectedRoute><MainLayout><DashboardPage /></MainLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><MainLayout><DashboardPage /></MainLayout></ProtectedRoute>} />
      <Route path="/legal/cgv" element={<MainLayout><CGVPage /></MainLayout>} />
      <Route path="/legal/privacy" element={<MainLayout><PrivacyPage /></MainLayout>} />
      <Route path="/legal/mentions" element={<MainLayout><MentionsPage /></MainLayout>} />
      <Route path="/secteur/:metier" element={<MainLayout><SecteurPage /></MainLayout>} />
      <Route path="/blog" element={<MainLayout><BlogPage /></MainLayout>} />
      <Route path="/about" element={<MainLayout><AboutPage /></MainLayout>} />
      <Route path="/contact" element={<MainLayout><ContactPage /></MainLayout>} />
      <Route path="/creation-site-web" element={<MainLayout><CreationSiteWebPage /></MainLayout>} />
      <Route path="/leads/:slug" element={<LeadsStaticPage />} />
      <Route path="*" element={<MainLayout><div className="container-custom py-16 text-center"><h1 className="text-6xl font-bold text-[#E2E8F0] mb-4">404</h1><h2 className="text-2xl font-bold text-[#0F172A] mb-4">Page non trouvée</h2><p className="text-[#64748B] mb-8">La page que vous recherchez n'existe pas.</p><a href="/" className="text-[#2563EB] hover:underline">Retour à l'accueil</a></div></MainLayout>} />
      <Route path="/:slug" element={<MainLayout><SEOPageRouter /></MainLayout>} />
      <Route path="/checkout" element={<MainLayout><CheckoutPage /></MainLayout>} />
      <Route path="/admin" element={<AdminRoute><MainLayout><AdminDashboard /></MainLayout></AdminRoute>} />
    </Routes>
    </Suspense>
    </>
  );
}

const LegalPage = ({ type }) => {
  const titles = {
    cgv: 'Conditions Générales de Vente',
    privacy: 'Politique de Confidentialité',
    mentions: 'Mentions Légales',
  };

  return (
    <div className="container-custom py-16">
      <h1 className="text-3xl font-bold text-[#0F172A] mb-8">{titles[type]}</h1>
      {type === 'cgv' && (
        <div className="prose max-w-none">
          <h2>1. Objet</h2>
          <p>Les présentes conditions générales de vente régissent l'utilisation du service LeadForge France.</p>
          <h2>2. Services</h2>
          <p>LeadForge France propose l'achat de fichiers de leads B2B issus de sources publiques officielles.</p>
          <h2>3. Prix</h2>
          <p>Les prix sont indiqués en euros TTC. Le paiement s'effectue en ligne par carte bancaire.</p>
          <h2>4. Livraison</h2>
          <p>Les fichiers sont disponibles au téléchargement immédiatement après le paiement.</p>
          <h2>5. Source des données</h2>
          <p>Les données proviennent du registre officiel INSEE SIRENE et de sources publiques accessibles légalement.</p>
        </div>
      )}
      {type === 'privacy' && (
        <div className="prose max-w-none">
          <h2>1. Collecte des données</h2>
          <p>Nous collectons uniquement les données nécessaires à la fourniture de nos services.</p>
          <h2>2. Utilisation</h2>
          <p>Vos données sont utilisées pour gérer votre compte et vos commandes.</p>
          <h2>3. Données des leads</h2>
          <p>Les données des entreprises proviennent de sources publiques officielles (INSEE SIRENE) et sont conformes au RGPD.</p>
          <h2>4. Vos droits</h2>
          <p>Vous disposez d'un droit d'accès, de rectification et de suppression de vos données personnelles.</p>
          <h2>5. Contact</h2>
          <p>Pour toute question : contact@leadforgefrance.com</p>
        </div>
      )}
      {type === 'mentions' && (
        <div className="prose max-w-none">
          <h2>Éditeur</h2>
          <p>LeadForge France</p>
          <h2>Contact</h2>
          <p>Email : contact@leadforgefrance.com</p>
          <h2>Hébergement</h2>
          <p>Site hébergé en France.</p>
          <h2>Source des données</h2>
          <p>Données issues du registre officiel INSEE SIRENE.</p>
          <p className="text-sm text-[#64748B] mt-4">Données professionnelles issues de sources publiques. Conformité RGPD assurée.</p>
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ConfigProvider>
          <AuthProvider>
            <AnalyticsWrapper>
              <ErrorBoundary>
                <AppRoutes />
              </ErrorBoundary>
            </AnalyticsWrapper>
            <Toaster
              position="top-right"
              richColors
              closeButton
              toastOptions={{
                style: {
                  fontFamily: 'Inter, sans-serif',
                },
              }}
            />
          </AuthProvider>
        </ConfigProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
